<template>
  <div>
    <!-- 导航栏 -->
    <header class="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-900 to-purple-900 text-white">
      <div class="container mx-auto px-4 md:px-8 max-w-6xl">
        <div class="flex justify-between items-center h-16">
          <a href="#" class="text-2xl font-bold">{{ $t('hero.title') }}</a>
          
          <!-- 桌面版导航菜单 -->
          <nav class="hidden md:block">
            <ul class="flex space-x-6">
              <li><a href="#game" class="hover:text-blue-200 transition-colors duration-300 relative group">{{ $t('nav.play') }}
                <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-200 transition-all duration-300 group-hover:w-full"></span>
              </a></li>
              <li><a href="#about" class="hover:text-blue-200 transition-colors duration-300 relative group">{{ $t('nav.about') }}
                <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-200 transition-all duration-300 group-hover:w-full"></span>
              </a></li>
              <li><a href="#features" class="hover:text-blue-200 transition-colors duration-300 relative group">{{ $t('nav.features') }}
                <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-200 transition-all duration-300 group-hover:w-full"></span>
              </a></li>
              <li><a href="#tips" class="hover:text-blue-200 transition-colors duration-300 relative group">{{ $t('nav.tips') }}
                <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-200 transition-all duration-300 group-hover:w-full"></span>
              </a></li>
              <li><a href="#faq" class="hover:text-blue-200 transition-colors duration-300 relative group">{{ $t('nav.faq') }}
                <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-200 transition-all duration-300 group-hover:w-full"></span>
              </a></li>
            </ul>
          </nav>
          
          <!-- 语言切换器 -->
          <LanguageSwitcher />
          
          <!-- 移动端菜单按钮 -->
          <button class="md:hidden" @click="isMobileMenuOpen = !isMobileMenuOpen" aria-label="Toggle menu">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
      
      <!-- 移动端导航菜单 -->
      <div class="container mx-auto px-4 md:px-8 max-w-6xl md:hidden" :class="{ 'hidden': !isMobileMenuOpen }">
        <ul class="py-4 space-y-2">
          <li><a href="#game" class="block py-2 hover:text-blue-200 transition-colors duration-300">{{ $t('nav.play') }}</a></li>
          <li><a href="#about" class="block py-2 hover:text-blue-200 transition-colors duration-300">{{ $t('nav.about') }}</a></li>
          <li><a href="#features" class="block py-2 hover:text-blue-200 transition-colors duration-300">{{ $t('nav.features') }}</a></li>
          <li><a href="#tips" class="block py-2 hover:text-blue-200 transition-colors duration-300">{{ $t('nav.tips') }}</a></li>
          <li><a href="#faq" class="block py-2 hover:text-blue-200 transition-colors duration-300">{{ $t('nav.faq') }}</a></li>
        </ul>
      </div>
    </header>

    <!-- 主要内容 -->
    <main class="pt-16">
      <!-- 英雄区域 -->
      <section class="relative bg-gradient-to-br from-blue-900 to-purple-900 text-white py-20 mb-12 rounded-xl overflow-hidden">
        <!-- 移除图片背景 -->
        <div class="absolute inset-0 bg-gradient-to-br from-blue-600/30 to-purple-600/30"></div>
        <!-- 添加动态背景元素 -->
        <div class="absolute inset-0 overflow-hidden">
          <div class="absolute -top-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div class="absolute top-0 -right-4 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div class="absolute -bottom-8 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
        <div class="relative z-10 text-center px-4 md:px-8 max-w-4xl mx-auto">
          <h1 class="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-white animate-fade-in">{{ $t('hero.title') }}</h1>
          <p class="text-xl md:text-2xl lg:text-3xl text-blue-100 mb-10 mx-auto animate-fade-in animation-delay-200">{{ $t('hero.subtitle') }}</p>
          <div class="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
            <a href="#game" class="btn bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-10 py-5 text-xl rounded-lg font-medium transition-all duration-300 transform hover:scale-105 inline-flex items-center space-x-2 animate-fade-in animation-delay-400">
              <span>{{ $t('hero.playButton') }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </a>
            <a href="#about" class="btn bg-white bg-opacity-10 hover:bg-opacity-20 text-white px-10 py-5 text-xl rounded-lg font-medium transition-all duration-300 transform hover:scale-105 inline-flex items-center space-x-2 animate-fade-in animation-delay-600">
              <span>{{ $t('hero.learnButton') }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      <!-- 游戏区域 -->
      <section id="game" class="mb-20 max-w-6xl mx-auto px-4">
        <div class="bg-white rounded-xl shadow-2xl overflow-hidden">
          <div class="relative w-full" style="padding-top: 56.25%;">
            <iframe 
              ref="gameFrame"
              :src="gameUrl"
              class="absolute inset-0 w-full h-full"
              frameborder="0"
              scrolling="no"
              allowfullscreen
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-modals"
              allow="autoplay; fullscreen"
            ></iframe>
            <!-- 全屏按钮 -->
            <button 
              @click="toggleFullscreen"
              class="absolute bottom-4 right-4 bg-white bg-opacity-80 hover:bg-opacity-100 text-gray-800 p-2 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
              :title="isFullscreen ? '退出全屏' : '全屏'"
            >
              <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 9h6v6H9V9zm0-8h6v6H9V1zm0 16h6v6H9v-6zM1 9h6v6H1V9zm0-8h6v6H1V1zm0 16h6v6H1v-6z" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <!-- 评论组件 -->
      <GameReview />

      <!-- 关于部分 -->
      <section id="about" class="mb-20 max-w-6xl mx-auto">
        <h2 class="text-center text-3xl md:text-4xl lg:text-5xl font-bold mb-8">{{ $t('about.title') }}</h2>
        <div class="grid md:grid-cols-2 gap-10 text-lg">
          <div>
            <p>{{ $t('about.description1') }}</p>
            <p>{{ $t('about.description2') }}</p>
          </div>
          <div>
            <p>{{ $t('about.description3') }}</p>
            <p>{{ $t('about.description4') }}</p>
          </div>
        </div>
      </section>

      <!-- 特性部分 -->
      <section id="features" class="mb-20 max-w-6xl mx-auto">
        <h2 class="text-center text-3xl md:text-4xl lg:text-5xl font-bold mb-10">{{ $t('features.title') }}</h2>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div class="text-blue-500 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h3 class="text-2xl font-semibold mb-4 text-gray-800">{{ $t('features.characters.title') }}</h3>
            <p class="text-gray-600 text-lg">{{ $t('features.characters.description') }}</p>
          </div>
          
          <div class="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div class="text-blue-500 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 class="text-2xl font-semibold mb-4 text-gray-800">{{ $t('features.weapons.title') }}</h3>
            <p class="text-gray-600 text-lg">{{ $t('features.weapons.description') }}</p>
          </div>
          
          <div class="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div class="text-blue-500 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 class="text-2xl font-semibold mb-4 text-gray-800">{{ $t('features.gameplay.title') }}</h3>
            <p class="text-gray-600 text-lg">{{ $t('features.gameplay.description') }}</p>
          </div>
        </div>
      </section>

      <!-- 游戏提示部分 -->
      <section id="tips" class="mb-20 max-w-6xl mx-auto">
        <h2 class="text-center text-3xl md:text-4xl lg:text-5xl font-bold mb-10">{{ $t('tips.title') }}</h2>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div class="text-blue-500 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 class="text-2xl font-semibold mb-4 text-gray-800">{{ $t('tips.tip1.title') }}</h3>
            <p class="text-gray-600 text-lg">{{ $t('tips.tip1.description') }}</p>
          </div>
          
          <div class="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div class="text-blue-500 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 class="text-2xl font-semibold mb-4 text-gray-800">{{ $t('tips.tip2.title') }}</h3>
            <p class="text-gray-600 text-lg">{{ $t('tips.tip2.description') }}</p>
          </div>
          
          <div class="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 border border-gray-100">
            <div class="text-blue-500 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h3 class="text-2xl font-semibold mb-4 text-gray-800">{{ $t('tips.tip3.title') }}</h3>
            <p class="text-gray-600 text-lg">{{ $t('tips.tip3.description') }}</p>
          </div>
        </div>
      </section>

      <!-- FAQ部分 -->
      <section id="faq" class="mb-20 max-w-6xl mx-auto">
        <h2 class="text-center text-3xl md:text-4xl lg:text-5xl font-bold mb-10">{{ $t('faq.title') }}</h2>
        <div class="space-y-4">
          <div v-for="(faq, index) in faqs" :key="index" class="bg-white rounded-xl shadow-sm overflow-hidden">
            <button 
              class="faq-toggle w-full text-left p-8 focus:outline-none flex justify-between items-center"
              @click="toggleFaq(index)"
            >
              <h3 class="text-2xl font-semibold text-gray-800">{{ faq.question }}</h3>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                class="h-8 w-8 text-blue-600 transform transition-transform"
                :class="{ 'rotate-180': faq.isOpen }"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div 
              class="faq-content px-8 pb-8"
              :class="{ 'hidden': !faq.isOpen }"
            >
              <p class="text-gray-600 text-lg">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 邮件订阅部分 -->
      <section class="mb-20 max-w-6xl mx-auto">
        <div class="bg-gradient-to-r from-blue-900 to-purple-900 text-white rounded-xl p-8 md:p-12 text-center">
          <h2 class="text-3xl md:text-4xl font-bold mb-4">{{ $t('newsletter.title') }}</h2>
          <p class="text-lg mb-8">{{ $t('newsletter.description') }}</p>
          <form class="max-w-md mx-auto" @submit.prevent="handleNewsletterSubmit">
            <div class="flex flex-col md:flex-row gap-4">
              <input 
                type="email" 
                v-model="email"
                :placeholder="$t('newsletter.placeholder')" 
                class="flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
              <button 
                type="submit" 
                class="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                {{ $t('newsletter.button') }}
              </button>
            </div>
          </form>
        </div>
      </section>

      <!-- 相关游戏部分 -->
      <section class="mb-20 max-w-6xl mx-auto">
        <h2 class="text-center text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{{ $t('related.title') }}</h2>
        <p class="text-center text-gray-600 text-lg mb-10">{{ $t('related.description') }}</p>
        <div class="grid md:grid-cols-3 gap-8">
          <div v-for="game in relatedGames" :key="game.id" class="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300">
            <!-- 移除图片,改用渐变背景 -->
            <div class="h-48 bg-gradient-to-br from-blue-500 to-purple-500"></div>
            <div class="p-6">
              <h3 class="text-xl font-semibold mb-2">{{ game.title }}</h3>
              <p class="text-gray-600">{{ game.description }}</p>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- 页脚 -->
    <footer class="bg-gray-900 text-white py-12">
      <div class="container mx-auto px-4 md:px-8 max-w-6xl">
        <div class="grid md:grid-cols-3 gap-8">
          <div>
            <h3 class="text-xl font-semibold mb-4">{{ $t('footer.quickLinks') }}</h3>
            <ul class="space-y-2">
              <li><a href="#game" class="text-apple-gray-medium hover:text-white transition-colors">{{ $t('nav.play') }}</a></li>
              <li><a href="#about" class="text-apple-gray-medium hover:text-white transition-colors">{{ $t('nav.about') }}</a></li>
              <li><a href="#features" class="text-apple-gray-medium hover:text-white transition-colors">{{ $t('nav.features') }}</a></li>
              <li><a href="#tips" class="text-apple-gray-medium hover:text-white transition-colors">{{ $t('nav.tips') }}</a></li>
              <li><a href="#faq" class="text-apple-gray-medium hover:text-white transition-colors">{{ $t('nav.faq') }}</a></li>
            </ul>
          </div>
          <div>
            <h3 class="text-xl font-semibold mb-4">{{ $t('footer.social') }}</h3>
            <div class="flex space-x-4">
              <a href="#" class="text-apple-gray-medium hover:text-white transition-colors">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="#" class="text-apple-gray-medium hover:text-white transition-colors">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            </div>
          </div>
          <div>
            <p class="text-apple-gray-medium">{{ $t('footer.copyright') }}</p>
            <p class="text-apple-gray-medium mt-2">
              <a href="/sitemap.html" class="text-blue-400 hover:text-blue-300 transition-colors">站点地图</a> | 
              <a href="/sitemap.xml" class="text-blue-400 hover:text-blue-300 transition-colors">XML</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from './components/LanguageSwitcher.vue'
import GameReview from './components/GameReview.vue'

const { t } = useI18n()
const isMobileMenuOpen = ref(false)
const email = ref('')
const gameFrame = ref(null)
const isFullscreen = ref(false)
const reviews = ref([])
const newReview = ref({
  rating: 0,
  comment: ''
})

const faqs = ref([
  {
    question: 'Is Monster Survivors free to play?',
    answer: 'Yes, Monster Survivors is completely free to play in your web browser. No download or installation required!',
    isOpen: false
  },
  {
    question: 'How do I play Monster Survivors?',
    answer: 'Simply click the "Play Monster Survivors Now" button above to start playing. Use WASD to move, Space to attack, and E for special abilities. No account required!',
    isOpen: false
  },
  {
    question: 'What makes Monster Survivors unique?',
    answer: 'Monster Survivors features unique character progression, weapon evolution system, and procedurally generated levels. Each playthrough offers a different experience!',
    isOpen: false
  }
])

const relatedGames = ref([
  {
    id: 1,
    title: 'Vampire Survivors',
    description: 'Similar roguelike survival gameplay with vampire theme.',
    image: 'images/related-game-1.jpg'
  },
  {
    id: 2,
    title: '20 Minutes Till Dawn',
    description: 'Fast-paced survival with unique weapon combinations.',
    image: 'images/related-game-2.jpg'
  },
  {
    id: 3,
    title: 'Brotato',
    description: 'Roguelike action with unique character progression.',
    image: 'images/related-game-3.jpg'
  }
])

const toggleFaq = (index) => {
  faqs.value[index].isOpen = !faqs.value[index].isOpen
}

const handleNewsletterSubmit = () => {
  // 这里添加订阅逻辑
  console.log('Newsletter subscription:', email.value)
  email.value = ''
}

const gameUrl = 'https://cloud.onlinegames.io/games/2025/unity/monster-survivors/index-og.html'

// 全屏切换
const toggleFullscreen = async () => {
  try {
    if (!document.fullscreenElement) {
      await gameFrame.value.requestFullscreen()
      isFullscreen.value = true
    } else {
      await document.exitFullscreen()
      isFullscreen.value = false
    }
  } catch (err) {
    console.error('全屏切换失败:', err)
    // 降级处理
    const iframe = gameFrame.value
    if (iframe) {
      if (!isFullscreen.value) {
        iframe.style.position = 'fixed'
        iframe.style.top = '0'
        iframe.style.left = '0'
        iframe.style.width = '100vw'
        iframe.style.height = '100vh'
        iframe.style.zIndex = '9999'
        document.body.style.overflow = 'hidden'
        isFullscreen.value = true
      } else {
        iframe.style.position = ''
        iframe.style.top = ''
        iframe.style.left = ''
        iframe.style.width = ''
        iframe.style.height = ''
        iframe.style.zIndex = ''
        document.body.style.overflow = ''
        isFullscreen.value = false
      }
    }
  }
}

// 监听退出全屏事件
onMounted(() => {
  document.addEventListener('fullscreenchange', handleFullscreenChange)
  window.addEventListener('message', handleGameMessage)
  
  // 预加载游戏
  console.log('预加载游戏: ' + gameUrl)
})

onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange)
  window.removeEventListener('message', handleGameMessage)
})

const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement
}

const handleGameMessage = (event) => {
  // 处理来自游戏的消息
  console.log('收到游戏消息:', event.data)
  // 可以根据需要处理不同类型的消息
}

const submitReview = () => {
  if (newReview.value.rating === 0) {
    alert(t('reviews.ratingRequired'))
    return
  }
  
  const review = {
    id: Date.now(),
    rating: newReview.value.rating,
    comment: newReview.value.comment,
    date: new Date().toLocaleDateString()
  }
  
  reviews.value.unshift(review)
  newReview.value = { rating: 0, comment: '' }
  
  // 这里可以添加向后端发送评论的逻辑
  console.log('Review submitted:', review)
}
</script>

<style>
/* 基础动画 */
@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

.animation-delay-4000 {
  animation-delay: 4s;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out forwards;
}

/* 滚动相关样式 */
html {
  scroll-behavior: smooth;
}

/* 移动端菜单样式 */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* FAQ 动画 */
.faq-content {
  transition: all 0.3s ease-in-out;
}

/* 按钮悬停效果 */
.btn {
  position: relative;
  overflow: hidden;
}

.btn::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 5px;
  height: 5px;
  background: rgba(255, 255, 255, 0.5);
  opacity: 0;
  border-radius: 100%;
  transform: scale(1, 1) translate(-50%);
  transform-origin: 50% 50%;
}

.btn:hover::after {
  animation: ripple 1s ease-out;
}

@keyframes ripple {
  0% {
    transform: scale(0, 0);
    opacity: 0.5;
  }
  100% {
    transform: scale(20, 20);
    opacity: 0;
  }
}

/* 文本渐变色 */
.text-gradient {
  background: linear-gradient(to right, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style> 